import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { werkplekApi, type SignalenResponse, type WerkplekSignal } from '../services/professionalWerkplekApi';
import { WERKPLEK_SIGNALEN_KEY } from './ProfessionalDashboardSchil';

export const SEVERITY_STIJL: Record<WerkplekSignal['severity'], { rand: string; stip: string }> = {
  urgent: { rand: 'border-l-red-500', stip: 'bg-red-500' },
  actie: { rand: 'border-l-gold-400', stip: 'bg-gold-400' },
  info: { rand: 'border-l-navy-200', stip: 'bg-navy-300' },
};

/** Tekstsleutel per signaal: sommige signalen hebben een variant op basis van hun parameters. */
function tekstSleutel(s: WerkplekSignal): string {
  if (s.key === 'verlenging_besluit') return s.params.besluit === 'approved' ? 'verlenging_goedgekeurd' : 'verlenging_afgewezen';
  if (s.key === 'b2b_uren_indienen' && s.params.afgewezen) return 'b2b_uren_afgewezen';
  if (s.key === 'actiepunt_deadline' && s.params.verlopen) return 'actiepunt_verlopen';
  if (s.key === 'pool_verzoek' && s.params.tegenvoorstel) return 'pool_tegenvoorstel';
  if (s.key === 'poortwachter_evaluatie' && s.params.verlopen) return 'poortwachter_verlopen';
  if (s.key === 'urensluiting') {
    if (s.params.achterstallig) return 'urensluiting_achterstallig';
    if (!s.params.fiat) return 'urensluiting_zonder_fiat';
    return s.params.laatste_dag ? 'urensluiting_laatste' : 'urensluiting';
  }
  if (s.key === 'onboarding') return `onboarding_${s.params.onderdeel}`;
  return s.key;
}

/** Signaal → titel + tekst. Klik markeert als gelezen en stuurt door naar de doelpagina. */
export function useSignaalWeergave() {
  const { t, i18n } = useTranslation('professional');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const taal = i18n.language?.startsWith('en') ? 'en' : 'nl';

  const datum = useMemo(() => new Intl.DateTimeFormat(taal === 'en' ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'short' }), [taal]);
  const relatief = useMemo(() => new Intl.RelativeTimeFormat(taal, { numeric: 'auto' }), [taal]);

  const relatieveTijd = (iso: string) => {
    const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
    if (Math.abs(min) < 60) return relatief.format(min, 'minute');
    const uur = Math.round(min / 60);
    if (Math.abs(uur) < 24) return relatief.format(uur, 'hour');
    return relatief.format(Math.round(uur / 24), 'day');
  };

  const tekst = (s: WerkplekSignal) => {
    if (s.source === 'notificatie') return { titel: s.title ?? '', tekst: s.content ?? '' };
    const p: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(s.params)) if (v !== null && v !== undefined) p[k] = v;
    if (typeof p.datum === 'string') p.datum = datum.format(new Date(p.datum));
    if (!p.organisatie) p.organisatie = t('werkplek.onbekendeOrganisatie');
    if (typeof p.kanaal === 'string') p.kanaal = t(`werkplek.kanaal.${p.kanaal}`);
    const sleutel = tekstSleutel(s);
    return {
      titel: String(t(`werkplek.signalen.${sleutel}.titel`, p)),
      tekst: String(t(`werkplek.signalen.${sleutel}.tekst`, p)),
    };
  };

  const open = (s: WerkplekSignal) => {
    if (!s.isRead) {
      // Optimistisch op gelezen; het doorsturen wacht niet op de server.
      queryClient.setQueryData<SignalenResponse>(WERKPLEK_SIGNALEN_KEY, (oud) =>
        oud
          ? { ...oud, ongelezen: Math.max(0, oud.ongelezen - 1), signalen: oud.signalen.map((x) => (x.id === s.id ? { ...x, isRead: true } : x)) }
          : oud,
      );
      werkplekApi.markeerGelezen(s.id).catch(() => queryClient.invalidateQueries({ queryKey: WERKPLEK_SIGNALEN_KEY }));
    }
    navigate(s.deeplink);
  };

  return { tekst, open, relatieveTijd };
}

export const WerkplekSignaalLijst: React.FC<{ signalen: WerkplekSignal[]; compact?: boolean }> = ({ signalen, compact }) => {
  const { tekst, open, relatieveTijd } = useSignaalWeergave();
  return (
    <ul className="space-y-2">
      {signalen.map((s) => {
        const { titel, tekst: omschrijving } = tekst(s);
        const stijl = SEVERITY_STIJL[s.severity];
        return (
          <li key={s.id}>
            <button
              type="button"
              onClick={() => open(s)}
              className={`w-full text-left flex items-start gap-3 rounded-lg border border-gray-200 border-l-4 ${stijl.rand} bg-white ${
                compact ? 'px-3 py-2' : 'px-4 py-3'
              } hover:shadow-sm hover:border-gray-300 transition`}
            >
              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${s.isRead ? 'bg-transparent' : stijl.stip}`} />
              <span className="flex-1 min-w-0">
                <span className={`block text-sm ${s.isRead ? 'text-navy-700' : 'font-semibold text-navy-900'}`}>{titel}</span>
                {omschrijving && <span className="block text-xs text-navy-500 mt-0.5 line-clamp-2">{omschrijving}</span>}
              </span>
              <span className="flex items-center gap-1 text-xs text-navy-400 flex-shrink-0">
                {relatieveTijd(s.occurredAt)}
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};
